import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  Switch,
  Alert,
  Snackbar,
  Skeleton,
  Chip,
  IconButton,
  Autocomplete,
  FormControlLabel,
} from '@mui/material';
import { Save, Edit, Check, Close, Search } from '@mui/icons-material';
import { doc, getDoc, setDoc, collection, getDocs } from '../../services/firestoreCompat';
import { db } from '../../services/firebaseConfig';

// Printer colors
const PRINTER_COLORS: Record<string, string> = {
  printer1: '#1976d2',
  printer2: '#d32f2f',
  printer3: '#ed6c02',
  printer4: '#2e7d32',
};

const DEFAULT_PRINTERS = [
  { id: 'printer1', name: 'Sushi Kitchen .42' },
  { id: 'printer2', name: 'Pizza Kitchen .43' },
  { id: 'printer3', name: 'Kueche 3 Starters .45' },
  { id: 'printer4', name: 'Kasse Epson .46' },
];

// Brand derivation rules — MUST stay in sync with
// web-qr-order/src/services/menuService.ts#BRAND_RULES.
// Categories have no brand field in Firestore; we derive it from the name.
const BRAND_RULES: { match: RegExp; brand: 'saheeh' | 'pizza_brand' | 'both' }[] = [
  { match: /(sushi|maki|sashimi|nigiri|temaki|uramaki|onigiri|gunkan|crispy.?roll|inside[- ]?out|hosomaki|futomaki|tempura.?roll|daifuku|mochi|edamame|miso|wakame|dashi|gyoza|ramen|udon|donburi|wasabi|japan)/i, brand: 'saheeh' },
  { match: /(pizza|pizzen|pasta|spaghetti|penne|lasagn|ravioli|gnocchi|tagliatell|fettuccin|linguin|rigatoni|tortellini|risott|calzone|antipasti|antipasto|insalat|bruschett|focacci|panini|tiramisu|dolci|dolce|italien|italian|don.?vito|vito)/i, brand: 'pizza_brand' },
  { match: /(getränk|getraenk|drink|beverage|softdrink|coffee|kaffee|bier|beer|wein|wine|wasser|water|juice|saft|tee|latte|espresso|cola|fanta|sprite|extra|adds?[- ]?on|sauce|dip|beilage|sides?)/i, brand: 'both' },
  { match: /(dessert|sweet|süss|suess)/i, brand: 'both' },
];

function deriveBrandFromCategory(name: string): 'saheeh' | 'pizza_brand' | 'both' {
  const c = (name || '').trim();
  if (!c) return 'both';
  for (const rule of BRAND_RULES) if (rule.match.test(c)) return rule.brand;
  return 'both';
}

interface MenuCategory {
  name: string;
  brand: 'saheeh' | 'pizza_brand' | 'both';
  displayOrder: number;
}

interface PrinterConfig {
  printers: { id: string; name: string }[];
  categoryRouting: Record<string, string[]>; // category -> printer ids
  itemOverrides: Record<string, string[]>; // item id -> printer ids
  showOtherStationItems: boolean;
}

const DEFAULT_CONFIG: PrinterConfig = {
  printers: DEFAULT_PRINTERS,
  categoryRouting: {},
  itemOverrides: {},
  showOtherStationItems: false,
};

interface MenuItem {
  id: string;
  name: string;
  category?: string;
}

const PrinterRoutingPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<PrinterConfig>(DEFAULT_CONFIG);
  const [originalConfig, setOriginalConfig] = useState<PrinterConfig>(DEFAULT_CONFIG);
  const [editingPrinter, setEditingPrinter] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<'all' | 'saheeh' | 'pizza_brand' | 'both'>('all');
  const [overrideSearch, setOverrideSearch] = useState('');
  const [selectedOverrideItem, setSelectedOverrideItem] = useState<MenuItem | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const configDoc = await getDoc(doc(db, 'printer_routing', 'config'));
      if (configDoc.exists()) {
        const data = configDoc.data() as PrinterConfig;
        const loaded: PrinterConfig = {
          printers: data.printers || DEFAULT_PRINTERS,
          categoryRouting: data.categoryRouting || {},
          itemOverrides: data.itemOverrides || {},
          showOtherStationItems: data.showOtherStationItems ?? false,
        };
        setConfig(loaded);
        setOriginalConfig(loaded);
      }
    } catch (err) {
      console.warn('[PRINTER ROUTING] Failed to load config:', err);
    }

    // Load menu items for override search
    try {
      const snap = await getDocs(collection(db, 'menu_items'));
      const items: MenuItem[] = [];
      snap.forEach((d) => {
        const data = d.data();
        items.push({ id: d.id, name: data.name || d.id, category: data.category || '' });
      });
      setMenuItems(items);
    } catch (err) {
      console.warn('[PRINTER ROUTING] Failed to load menu items:', err);
    }

    // Build the category list from TWO sources so nothing is ever missed:
    //   1) `categories` collection (has display_order + explicit brand when set)
    //   2) distinct `category` strings on menu_items (covers legacy/ad-hoc categories
    //      that don't have a row in the `categories` collection yet)
    //
    // Brand is derived from the category name using the same regex the QR app uses
    // (see web-qr-order/src/services/menuService.ts#BRAND_RULES) because the
    // `categories` collection does NOT have a brand field — brand lives on menu
    // items, and category membership is implicitly brand-tagged by its name.
    try {
      const byName = new Map<string, MenuCategory>();

      // 1) Firestore `categories` collection
      try {
        const snap = await getDocs(collection(db, 'categories'));
        snap.forEach((d) => {
          const data: any = d.data();
          if (data.is_active === false) return;
          const name = (data.name || '').trim();
          if (!name) return;
          const explicit = (data.brand || '').toString().toLowerCase();
          const brand: MenuCategory['brand'] =
            explicit === 'saheeh' || explicit === 'pizza_brand' || explicit === 'both'
              ? (explicit as MenuCategory['brand'])
              : deriveBrandFromCategory(name);
          byName.set(name.toLowerCase(), {
            name,
            brand,
            displayOrder: typeof data.display_order === 'number' ? data.display_order : 999,
          });
        });
      } catch (e) {
        console.warn('[PRINTER ROUTING] categories collection load failed:', e);
      }

      // 2) Distinct category strings on menu_items — safety net for any category
      //    that's only referenced on items but has no `categories` row.
      try {
        const itemsSnap = await getDocs(collection(db, 'menu_items'));
        itemsSnap.forEach((d) => {
          const data: any = d.data();
          if (data.is_available === false) return;
          const name = (data.category || '').trim();
          if (!name) return;
          const key = name.toLowerCase();
          if (!byName.has(key)) {
            byName.set(key, {
              name,
              brand: deriveBrandFromCategory(name),
              displayOrder: 9999,
            });
          }
        });
      } catch (e) {
        console.warn('[PRINTER ROUTING] menu_items scan for categories failed:', e);
      }

      const cats = Array.from(byName.values()).sort((a, b) => {
        const brandOrder = { saheeh: 0, pizza_brand: 1, both: 2 } as const;
        if (brandOrder[a.brand] !== brandOrder[b.brand]) {
          return brandOrder[a.brand] - brandOrder[b.brand];
        }
        if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
        return a.name.localeCompare(b.name);
      });

      setCategories(cats);
    } catch (err) {
      console.warn('[PRINTER ROUTING] Failed to load categories:', err);
      setCategories([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'printer_routing', 'config'), {
        ...config,
        updated_at: new Date().toISOString(),
      }, { merge: true });
      setOriginalConfig({ ...config });
      setSnackbar({ open: true, message: 'Printer routing saved successfully', severity: 'success' });
    } catch (err) {
      console.error('[PRINTER ROUTING] Save failed:', err);
      setSnackbar({ open: true, message: 'Failed to save printer routing', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryPrinter = (category: string, printerId: string) => {
    setConfig(prev => {
      const current = prev.categoryRouting[category] || [];
      const updated = current.includes(printerId)
        ? current.filter(id => id !== printerId)
        : [...current, printerId];
      return { ...prev, categoryRouting: { ...prev.categoryRouting, [category]: updated } };
    });
  };

  const toggleItemOverride = (itemId: string, printerId: string) => {
    setConfig(prev => {
      const current = prev.itemOverrides[itemId] || [];
      const updated = current.includes(printerId)
        ? current.filter(id => id !== printerId)
        : [...current, printerId];
      const newOverrides = { ...prev.itemOverrides };
      if (updated.length === 0) {
        delete newOverrides[itemId];
      } else {
        newOverrides[itemId] = updated;
      }
      return { ...prev, itemOverrides: newOverrides };
    });
  };

  const startEditPrinter = (printerId: string) => {
    const printer = config.printers.find(p => p.id === printerId);
    if (printer) {
      setEditName(printer.name);
      setEditingPrinter(printerId);
    }
  };

  const saveEditPrinter = () => {
    if (!editingPrinter) return;
    setConfig(prev => ({
      ...prev,
      printers: prev.printers.map(p => p.id === editingPrinter ? { ...p, name: editName } : p),
    }));
    setEditingPrinter(null);
    setEditName('');
  };

  const addItemOverride = () => {
    if (!selectedOverrideItem) return;
    if (!config.itemOverrides[selectedOverrideItem.id]) {
      setConfig(prev => ({
        ...prev,
        itemOverrides: { ...prev.itemOverrides, [selectedOverrideItem.id]: [] },
      }));
    }
    setSelectedOverrideItem(null);
    setOverrideSearch('');
  };

  const removeItemOverride = (itemId: string) => {
    setConfig(prev => {
      const newOverrides = { ...prev.itemOverrides };
      delete newOverrides[itemId];
      return { ...prev, itemOverrides: newOverrides };
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Skeleton width={300} height={36} sx={{ mb: 3 }} />
          <Skeleton height={200} sx={{ borderRadius: 2.5, mb: 2 }} />
          <Skeleton height={400} sx={{ borderRadius: 2.5 }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
              Printer Routing
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
              Configure which printers receive orders for each category and item
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? null : <Save sx={{ fontSize: 16 }} />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: '#e0e0e0',
              '&:hover': { bgcolor: '#e0e0e0' },
              '&:disabled': { bgcolor: '#e0e0e0', color: '#6b7280' },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            You have unsaved changes. Click "Save Changes" to apply.
          </Alert>
        )}

        {/* Printers Section */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #3a3d52' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>Printers</Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
            {config.printers.map((printer) => (
              <Paper
                key={printer.id}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  border: `2px solid ${PRINTER_COLORS[printer.id]}`,
                  bgcolor: `${PRINTER_COLORS[printer.id]}08`,
                  minWidth: { xs: 'calc(50% - 4px)', md: 200 },
                  flex: { xs: '1 1 calc(50% - 4px)', md: '1 1 200px' },
                }}
              >
                {editingPrinter === printer.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveEditPrinter()}
                      autoFocus
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                    <IconButton size="small" onClick={saveEditPrinter} sx={{ color: '#4CAF50' }}>
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditingPrinter(null)} sx={{ color: '#6b7280' }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Chip
                        label={printer.id.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: PRINTER_COLORS[printer.id],
                          color: '#1a1d2e',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 20,
                          mb: 0.5,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0' }}>
                        {printer.name}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => startEditPrinter(printer.id)} sx={{ color: '#9ca3af' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* Category Routing */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #3a3d52' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>Category Routing</Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              Select which printer(s) should receive orders for each category.
              Unmapped categories fall back to defaults (POS, QR orders, and delivery all use this config).
            </Typography>
          </Box>

          {/* Filter bar: brand filter + search */}
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #3a3d52', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {(() => {
              const counts = {
                all: categories.length,
                saheeh: categories.filter(c => c.brand === 'saheeh').length,
                pizza_brand: categories.filter(c => c.brand === 'pizza_brand').length,
                both: categories.filter(c => c.brand === 'both').length,
              };
              const labels = {
                all: `All (${counts.all})`,
                saheeh: `Saheeh (${counts.saheeh})`,
                pizza_brand: `Pizza Brand (${counts.pizza_brand})`,
                both: `Shared (${counts.both})`,
              };
              return (['all', 'saheeh', 'pizza_brand', 'both'] as const).map((b) => {
                const active = brandFilter === b;
                return (
                  <Chip
                    key={b}
                    label={labels[b]}
                    size="small"
                    onClick={() => setBrandFilter(b)}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      bgcolor: active ? '#00C896' : 'transparent',
                      color: active ? '#1a1d2e' : '#9ca3af',
                      border: `1px solid ${active ? '#00C896' : '#3a3d52'}`,
                    }}
                  />
                );
              });
            })()}
            <TextField
              size="small"
              placeholder="Search categories..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, color: '#6b7280', mr: 0.5 }} /> }}
              sx={{ ml: 'auto', flex: '1 1 200px', maxWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>

          {(() => {
            const q = categorySearch.trim().toLowerCase();
            const visibleCategories = categories.filter((c) => {
              if (brandFilter !== 'all' && c.brand !== brandFilter) return false;
              if (q && !c.name.toLowerCase().includes(q)) return false;
              return true;
            });

            if (visibleCategories.length === 0) {
              return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                    No categories match this filter.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Brand is auto-detected from the category name (e.g. "Pizza", "Sushi").
                    If a category is in the wrong brand, rename it to include a cuisine keyword
                    or click "All" to see every category.
                  </Typography>
                </Box>
              );
            }

            const brandChip = (brand: MenuCategory['brand']) => {
              const cfg = {
                saheeh: { label: 'Saheeh', bg: '#00C896', color: '#1a1d2e' },
                pizza_brand: { label: 'Pizza Brand', bg: '#f59e0b', color: '#1a1d2e' },
                both: { label: 'Shared', bg: '#6366f1', color: '#fff' },
              }[brand];
              return (
                <Chip
                  label={cfg.label}
                  size="small"
                  sx={{
                    bgcolor: cfg.bg,
                    color: cfg.color,
                    fontWeight: 700,
                    fontSize: '0.62rem',
                    height: 18,
                    ml: 1,
                  }}
                />
              );
            };

            return (
              <>
                {/* Mobile Category Cards */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, p: 1.5 }}>
                  {visibleCategories.map(({ name: category, brand }) => (
                    <Box
                      key={category}
                      sx={{
                        backgroundColor: '#252838',
                        border: '1px solid #3a3d52',
                        borderRadius: '10px',
                        p: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                          {category}
                        </Typography>
                        {brandChip(brand)}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {config.printers.map((printer) => {
                          const isChecked = (config.categoryRouting[category] || []).includes(printer.id);
                          return (
                            <Chip
                              key={printer.id}
                              label={printer.name.split(' ')[0]}
                              size="small"
                              onClick={() => toggleCategoryPrinter(category, printer.id)}
                              sx={{
                                height: 26,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                bgcolor: isChecked ? PRINTER_COLORS[printer.id] : 'transparent',
                                color: isChecked ? '#fff' : '#9ca3af',
                                border: `1px solid ${PRINTER_COLORS[printer.id]}`,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Desktop Category Table */}
                <Box sx={{ overflow: 'auto', display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ minWidth: 600 }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', borderBottom: '1px solid #3a3d52', bgcolor: 'rgba(0, 200, 150,0.08)', px: 2.5, py: 1 }}>
                      <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Category</Typography>
                      </Box>
                      {config.printers.map((printer) => (
                        <Box key={printer.id} sx={{ width: 140, textAlign: 'center', flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: PRINTER_COLORS[printer.id] }}>
                            {printer.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {/* Category Rows */}
                    {visibleCategories.map(({ name: category, brand }) => (
                      <Box
                        key={category}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 2.5,
                          py: 0.5,
                          borderBottom: '1px solid #f5f5f5',
                          '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' },
                        }}
                      >
                        <Box sx={{ flex: '1 1 200px', minWidth: 200, display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>{category}</Typography>
                          {brandChip(brand)}
                        </Box>
                        {config.printers.map((printer) => (
                          <Box key={printer.id} sx={{ width: 140, textAlign: 'center', flexShrink: 0 }}>
                            <Checkbox
                              checked={(config.categoryRouting[category] || []).includes(printer.id)}
                              onChange={() => toggleCategoryPrinter(category, printer.id)}
                              size="small"
                              sx={{
                                color: '#ccc',
                                '&.Mui-checked': { color: PRINTER_COLORS[printer.id] },
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            );
          })()}
        </Paper>

        {/* Item Override Section */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', mb: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #3a3d52' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>Item Overrides</Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>Override category routing for specific menu items</Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            {/* Search and Add */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                options={menuItems.filter(i => !config.itemOverrides[i.id])}
                getOptionLabel={(opt) => `${opt.name}${opt.category ? ` (${opt.category})` : ''}`}
                value={selectedOverrideItem}
                onChange={(_, val) => setSelectedOverrideItem(val)}
                inputValue={overrideSearch}
                onInputChange={(_, val) => setOverrideSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search menu items..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <Search sx={{ fontSize: 18, color: '#6b7280', mr: 0.5 }} />,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={addItemOverride}
                disabled={!selectedOverrideItem}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#ddd', color: '#9ca3af' }}
              >
                Add Override
              </Button>
            </Box>

            {/* Override Items List */}
            {Object.keys(config.itemOverrides).length === 0 ? (
              <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 3 }}>
                No item overrides configured. Items will use category routing.
              </Typography>
            ) : (
              <>
              {/* Mobile Override Cards */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
                {Object.keys(config.itemOverrides).map((itemId) => {
                  const item = menuItems.find(m => m.id === itemId);
                  return (
                    <Box
                      key={itemId}
                      sx={{
                        backgroundColor: '#252838',
                        border: '1px solid #3a3d52',
                        borderRadius: '10px',
                        p: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>{item?.name || itemId}</Typography>
                          {item?.category && <Typography variant="caption" sx={{ color: '#6b7280' }}>{item.category}</Typography>}
                        </Box>
                        <IconButton size="small" onClick={() => removeItemOverride(itemId)} sx={{ color: '#6b7280', width: 28, height: 28 }}>
                          <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {config.printers.map((printer) => {
                          const isChecked = (config.itemOverrides[itemId] || []).includes(printer.id);
                          return (
                            <Chip
                              key={printer.id}
                              label={printer.name.split(' ')[0]}
                              size="small"
                              onClick={() => toggleItemOverride(itemId, printer.id)}
                              sx={{
                                height: 26,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                bgcolor: isChecked ? PRINTER_COLORS[printer.id] : 'transparent',
                                color: isChecked ? '#fff' : '#9ca3af',
                                border: `1px solid ${PRINTER_COLORS[printer.id]}`,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Desktop Override Table */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', borderBottom: '1px solid #3a3d52', pb: 1, mb: 1 }}>
                  <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Item</Typography>
                  </Box>
                  {config.printers.map((printer) => (
                    <Box key={printer.id} sx={{ width: 120, textAlign: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: PRINTER_COLORS[printer.id] }}>
                        {printer.name.split(' ')[0]}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ width: 40 }} />
                </Box>
                {/* Override Rows */}
                {Object.keys(config.itemOverrides).map((itemId) => {
                  const item = menuItems.find(m => m.id === itemId);
                  return (
                    <Box
                      key={itemId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.5,
                        borderBottom: '1px solid #f5f5f5',
                        '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' },
                      }}
                    >
                      <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>
                          {item?.name || itemId}
                        </Typography>
                        {item?.category && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>{item.category}</Typography>
                        )}
                      </Box>
                      {config.printers.map((printer) => (
                        <Box key={printer.id} sx={{ width: 120, textAlign: 'center', flexShrink: 0 }}>
                          <Checkbox
                            checked={(config.itemOverrides[itemId] || []).includes(printer.id)}
                            onChange={() => toggleItemOverride(itemId, printer.id)}
                            size="small"
                            sx={{
                              color: '#ccc',
                              '&.Mui-checked': { color: PRINTER_COLORS[printer.id] },
                            }}
                          />
                        </Box>
                      ))}
                      <Box sx={{ width: 40 }}>
                        <IconButton size="small" onClick={() => removeItemOverride(itemId)} sx={{ color: '#6b7280' }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              </>
            )}
          </Box>
        </Paper>

        {/* Settings */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, borderBottom: '1px solid #3a3d52' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>Settings</Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.showOtherStationItems}
                  onChange={(e) => setConfig(prev => ({ ...prev, showOtherStationItems: e.target.checked }))}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2E7D32' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2E7D32' },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>
                    Show other station items as reference
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    When enabled, printers will also show items routed to other stations (greyed out) for reference
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </Box>
        </Paper>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default PrinterRoutingPage;
